import * as Sentry from '@sentry/browser';
import analytics from 'commons/tracking/analytics';
import {
  SAMPLE_ORDER_ERROR,
  type SampleOrderStep,
} from './sampleOrderContract';

/**
 * Every event the sample order complex emits. See `docs/publishing/sample-order-journey.md` §3a.
 *
 * Three constraints the shapes below follow, all from the Adobe path: values stay scalar and free
 * of commas and colons, the one number that matters is also sent as `value`, and each call builds a
 * fresh object because `trackEvent` deletes `value` off the one it is handed.
 */

/** Which entry point the partner came in through. Nothing in the journey may branch on it. */
export const SAMPLE_ORDER_SOURCE = {
  COMPOSITION_PAGE: 'composition_page',
  PRODUCT_SETTINGS: 'product_settings',
  DASHBOARD: 'dashboard',
  NAVBAR: 'navbar',
  SHOP_SETTINGS: 'shop_settings',
} as const;

export type SampleOrderSource =
  (typeof SAMPLE_ORDER_SOURCE)[keyof typeof SAMPLE_ORDER_SOURCE];

interface WithSource {
  source: SampleOrderSource;
}

/** Inside the journey the scope is known, and it picks the sample shop, so every event carries it. */
interface InJourney extends WithSource {
  scope: string;
}

/**
 * Nothing here may throw: the journey calls these inside its control flow, so a failing sink would
 * reach the partner as the wrong error - or as no journey at all.
 */
const report = (name: string, props: Record<string, unknown>): void => {
  try {
    analytics.logEvent(name, props);
  } catch (failure) {
    console.warn(`[sampleOrder] could not report ${name}`, failure);
  }
};

/** The error set names its own members, so the name is the label: `NO_SHOP`, not `ERROR.NO_SHOP`. */
const ERROR_NAMES: Record<string, string> = Object.fromEntries(
  Object.entries(SAMPLE_ORDER_ERROR).map(([name, key]) => [key, name]),
);

const shortenReason = (reason: string) => ERROR_NAMES[reason] ?? reason;

export const trackSampleOrderEntryClick = ({
  source,
  scope,
  mode,
}: WithSource & { scope?: string; mode?: string }): void =>
  report('sample_order_entry_click', {
    source,
    ...(scope ? { scope } : {}),
    ...(mode ? { mode } : {}),
  });

export const trackSampleOrderChannelSelect = ({
  source,
  channel,
  skipped,
}: WithSource & {
  channel: string;
  skipped: boolean;
}): void =>
  report('sample_order_channel_select', {
    source,
    channel,
    skipped,
  });

export const trackSampleOrderCompositionSelect = ({
  source,
  skipped,
}: WithSource & { skipped: boolean }): void =>
  report('sample_order_composition_select', { source, skipped });

/** A composition with no sellables was picked — the one case the dialog has to refuse. */
export const trackSampleOrderCompositionBlocked = ({
  source,
}: WithSource): void => report('sample_order_composition_blocked', { source });

/** `selectable`, not `products`: that name means "selected" on every other event. */
export const trackSampleOrderOpen = ({
  source,
  scope,
  selectable,
  sellables,
}: InJourney & { selectable: number; sellables: number }): void =>
  report('sample_order_open', {
    source,
    scope,
    value: selectable,
    selectable,
    sellables,
  });

export const trackSampleOrderConfigure = ({
  source,
  scope,
  products,
  selectAll,
}: InJourney & { products: number; selectAll: boolean }): void =>
  report('sample_order_configure', {
    source,
    scope,
    value: products,
    products,
    selectAll,
  });

export const trackSampleOrderCheckoutBlocked = ({
  source,
  scope,
  reason,
  lines,
}: InJourney & { reason: string; lines: number }): void =>
  report('sample_order_checkout_blocked', {
    source,
    scope,
    reason,
    value: lines,
    lines,
  });

/** The last thing PA can see; whether the order is paid for is only in the shops' order data. */
export const trackSampleOrderCheckout = ({
  source,
  scope,
  total,
  products,
  samples,
  currency,
}: InJourney & {
  total: number;
  products: number;
  samples: number;
  currency: number | string;
}): void =>
  report('sample_order_checkout', {
    source,
    scope,
    value: total,
    total,
    products,
    samples,
    currency,
  });

export const trackSampleOrderError = ({
  source,
  scope,
  reason,
  step,
}: InJourney & { reason: string; step: SampleOrderStep }): void =>
  report('sample_order_error', {
    source,
    scope,
    reason: shortenReason(reason),
    step,
  });

export const trackSampleOrderAbandon = ({
  source,
  scope,
  step,
  products,
}: InJourney & { step: SampleOrderStep; products: number }): void =>
  report('sample_order_abandon', {
    source,
    scope,
    step,
    value: products,
    products,
  });

export const trackSampleOrderHintShown = ({ source }: WithSource): void =>
  report('sample_order_hint_shown', { source });

/** `via: 'anchor'` is a partner who pressed the button the hint explains - a conversion. */
export const trackSampleOrderHintDismissed = ({
  source,
  via,
}: WithSource & { via: 'close' | 'anchor' }): void =>
  report('sample_order_hint_dismissed', { source, via });

/**
 * The price cross-check reports to Sentry, not through `report` above: `analytics` is silent for
 * admin sessions and without cookie consent, so no in-house test could ever produce an event and
 * an empty result would say nothing. See the journey doc §7.
 */
interface PriceCrossCheck {
  source: SampleOrderSource;
  sellableId: string;
  productTypeId: number;
  shopId: number | undefined;
  currencyId: number | string | undefined;
}

/** Called through an object so specs can spy: a `vi.mock` of the SDK registers too late here. */
export const sentry = {
  withScope: Sentry.withScope,
  captureMessage: Sentry.captureMessage,
};

const capture = (message: string, props: Record<string, unknown>): void => {
  if (!SPRD.ERROR_LOGGING_ACTIVE) return;

  try {
    sentry.withScope((scope) => {
      scope.setLevel('warning');
      // Without this Sentry opens an issue per sellable id.
      scope.setFingerprint([message]);
      scope.setTag('productTypeId', String(props.productTypeId));
      scope.setTag('sampleShopId', String(props.shopId));
      scope.setExtras(props);
      sentry.captureMessage(message);
    });
  } catch (failure) {
    console.warn(`[sampleOrder] could not report ${message}`, failure);
  }
};

/**
 * `deltaUnit` is what classifies the two price terms PA does not model: a positive multiple of a
 * print type fee, or minus one `printAreaPrice` per additional area. The doc's §5 has the table.
 */
export const trackSampleOrderPriceMismatch = ({
  displayedUnit,
  displayedTotal,
  basket,
  ...rest
}: PriceCrossCheck & {
  displayedUnit: number;
  displayedTotal: number | null;
  basket: number;
  quantity: number;
  printAreaCount: number;
  printAreaCountCertain: boolean;
}): void =>
  capture('sample order price mismatch', {
    ...rest,
    displayedUnit,
    displayedTotal,
    basket,
    deltaUnit: Number((basket - displayedUnit).toFixed(2)),
  });

/** The cross-check could not run at all, which is worse than a mismatch and just as quiet. */
export const trackSampleOrderPriceUnverified = (props: PriceCrossCheck): void =>
  capture('sample order price not verifiable', { ...props });
